import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { Filter, Loader2, MessageSquare, Pencil, RefreshCw, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useUrlState } from "@/hooks/use-url-state";
import { getAuthHeaders } from "@/lib/queryClient";
import { EmailThreadItem } from "@/components/email/email-thread-item";

type EntityType = "contact" | "account" | "deal" | "client_group";
type DateRange = "all" | "7d" | "30d" | "90d";
/** "all" = activity + notes; "email" = the Communications timeline (emails only). */
type FeedLens = "all" | "email";

type FeedItem = {
  id: string;
  itemType: "activity" | "note" | "email";
  entityType: EntityType;
  entityId: string;
  occurredAt: string;
  actorUserId: string | null;
  actorName: string | null;
  eventType: string;
  title: string;
  body: string | null;
  noteId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  isEditable: boolean;
  // email-only fields (present when itemType === "email")
  threadId?: string | null;
  subject?: string | null;
  preview?: string | null;
  direction?: "inbound" | "outbound" | null;
  messageCount?: number;
  hasAttachments?: boolean;
  latestMessageId?: string;
  outlookWebLink?: string | null;
};

type FeedResponse = {
  currentUserId: string | null;
  items: FeedItem[];
  nextCursor: { cursorTs: string; cursorId: string } | null;
};

type NoteResponse = {
  id: string;
  body: string;
  authorUserId: string | null;
  authorName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  isEditable: boolean;
};

interface ActivityFeedProps {
  entityType: EntityType;
  entityId: string;
  testId?: string;
  /**
   * "all" (default) renders the activity + notes feed. "email" renders the
   * read-only Communications timeline (email/thread items only, no note composer).
   */
  lens?: FeedLens;
}

const DATE_RANGE_OPTIONS: Array<{ value: DateRange; label: string }> = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const ENTITY_LABELS: Record<EntityType, string> = {
  contact: "Contact",
  account: "Account",
  deal: "Deal",
  client_group: "Client Group",
};

const EVENT_GROUPS = [
  { value: "stage", label: "Stage Changes", eventTypes: ["deal_status_changed", "stage_changed", "scenario_promoted_to_deal"] },
  { value: "documents", label: "Documents", eventTypes: ["document_uploaded", "document_status_changed", "document_deleted"] },
  { value: "tasks", label: "Tasks", eventTypes: ["task_created", "task_completed", "task_deleted"] },
  { value: "comments", label: "Comments", eventTypes: ["comment_added", "comment_edited", "comment_deleted", "note"] },
  { value: "parties", label: "Parties", eventTypes: ["party_added", "party_removed"] },
  { value: "deal", label: "Deal", eventTypes: ["deal_created", "deal_updated", "deal_assigned"] },
  { value: "facilities", label: "Facilities", eventTypes: ["facility_created", "facility_updated", "facility_status_changed"] },
  { value: "securities", label: "Securities", eventTypes: ["security_added", "security_removed"] },
] as const;

async function fetchJson<T>(url: string): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(url, {
    headers: authHeaders,
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = (await response.text()) || response.statusText;
    throw new Error(`${response.status}: ${errorText}`);
  }

  return response.json() as Promise<T>;
}

async function sendJson<T>(method: string, url: string, body?: unknown): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(url, {
    method,
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorText = (await response.text()) || response.statusText;
    throw new Error(`${response.status}: ${errorText}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

function formatOccurredAt(value: string) {
  return new Date(value).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function getDateGroupLabel(value: string) {
  const date = new Date(value);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (entryDate.getTime() === today.getTime()) return "Today";
  if (entryDate.getTime() === yesterday.getTime()) return "Yesterday";

  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatEventLabel(value: string) {
  if (value === "note") return "Note";
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeEventType(value: string | null | undefined) {
  return value && value.length > 0 ? value : "all";
}

function matchesEventFilter(item: FeedItem, activeFilter: string) {
  if (activeFilter === "all") return true;
  const group = EVENT_GROUPS.find((option) => option.value === activeFilter);
  if (group) {
    return (group.eventTypes as readonly string[]).includes(item.eventType);
  }
  return item.eventType === activeFilter;
}

function NoteItem({
  item,
  editValue,
  isEditing,
  isSaving,
  onStartEdit,
  onCancelEdit,
  onEditValueChange,
  onSaveEdit,
  onDelete,
}: {
  item: FeedItem;
  editValue: string;
  isEditing: boolean;
  isSaving: boolean;
  onStartEdit: (item: FeedItem) => void;
  onCancelEdit: () => void;
  onEditValueChange: (value: string) => void;
  onSaveEdit: () => void;
  onDelete: (item: FeedItem) => void;
}) {
  return (
    <div className="rounded-md border bg-muted/20 p-3" data-testid="activity-feed-note">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">Note</Badge>
          <span className="text-xs text-muted-foreground">
            {item.actorName || item.actorUserId || "Unknown author"} · {formatOccurredAt(item.occurredAt)}
          </span>
        </div>
        {item.isEditable ? (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onStartEdit(item)}
              data-testid={`button-edit-note-${item.id}`}
            >
              <Pencil className="mr-1 h-3 w-3" />
              Edit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-destructive"
              onClick={() => onDelete(item)}
              data-testid={`button-delete-note-${item.id}`}
            >
              <Trash2 className="mr-1 h-3 w-3" />
              Delete
            </Button>
          </div>
        ) : null}
      </div>

      {isEditing ? (
        <div className="mt-3 space-y-2">
          <Textarea
            value={editValue}
            onChange={(event) => onEditValueChange(event.target.value)}
            className="min-h-[90px]"
            data-testid="activity-feed-note-edit"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!editValue.trim() || isSaving}
              onClick={onSaveEdit}
              data-testid="button-save-note-edit"
            >
              {isSaving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onCancelEdit}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-3 whitespace-pre-wrap text-sm">{item.body}</p>
      )}
    </div>
  );
}

export function ActivityFeed({
  entityId,
  entityType,
  testId = "panel-activity-feed",
  lens = "all",
}: ActivityFeedProps) {
  const isEmailLens = lens === "email";
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKeyBase = `/api/crm/${entityType}/${entityId}/feed`;

  const [dateRange, setDateRange] = useUrlState<DateRange>("activityDateRange", "all");
  const [eventType, setEventType] = useUrlState("activityEventType", "all");
  const [actor, setActor] = useUrlState("activityActor", "all");
  const [entityFilter, setEntityFilter] = useUrlState("activityEntityType", "all");
  const [search, setSearch] = useUrlState("activitySearch", "", { debounceMs: 300 });

  const [draftDateRange, setDraftDateRange] = useState<DateRange>(dateRange);
  const [draftEventType, setDraftEventType] = useState(eventType);
  const [draftActor, setDraftActor] = useState(actor);
  const [draftEntityFilter, setDraftEntityFilter] = useState(entityFilter);

  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setDraftDateRange(dateRange);
    setDraftEventType(eventType);
    setDraftActor(actor);
    setDraftEntityFilter(entityFilter);
  }, [actor, dateRange, entityFilter, eventType]);

  const queryKey = [
    queryKeyBase,
    {
      lens,
      dateRange,
      actor: isEmailLens || normalizeEventType(actor) === "all" ? undefined : actor,
      eventType: isEmailLens || normalizeEventType(eventType) === "all" ? undefined : eventType,
      entityFilter: entityFilter === "all" ? undefined : entityFilter,
      q: search || undefined,
      includeNotes: !isEmailLens,
      includeEmails: isEmailLens,
    },
  ];

  const feedQuery = useInfiniteQuery({
    queryKey,
    initialPageParam: null as FeedResponse["nextCursor"] | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("dateRange", dateRange);
      if (isEmailLens) {
        // Communications timeline: emails only, no notes, no activity rows.
        params.set("includeEmails", "true");
        params.set("includeNotes", "false");
        params.set("eventType", "email");
      } else {
        params.set("includeNotes", "true");
        if (actor !== "all") params.set("actor", actor);
        if (eventType !== "all") params.set("eventType", eventType);
      }
      if (search.trim().length > 0) params.set("q", search.trim());
      if (pageParam?.cursorTs) params.set("cursorTs", pageParam.cursorTs);
      if (pageParam?.cursorId) params.set("cursorId", pageParam.cursorId);

      return fetchJson<FeedResponse>(`${queryKeyBase}?${params.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: Boolean(entityId),
  });

  const flatItems = useMemo(() => {
    const base = feedQuery.data?.pages.flatMap((page) => page.items) ?? [];
    return base.filter((item) => {
      if (entityFilter !== "all" && item.entityType !== entityFilter) {
        return false;
      }
      return matchesEventFilter(item, eventType);
    });
  }, [entityFilter, eventType, feedQuery.data?.pages]);

  const currentUserId = feedQuery.data?.pages[0]?.currentUserId ?? null;

  const actorOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of flatItems) {
      if (item.actorUserId) {
        map.set(item.actorUserId, item.actorName || item.actorUserId);
      }
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [flatItems]);

  const groupedItems = useMemo(() => {
    const groups = new Map<string, FeedItem[]>();
    for (const item of flatItems) {
      const label = getDateGroupLabel(item.occurredAt);
      const existing = groups.get(label) ?? [];
      existing.push(item);
      groups.set(label, existing);
    }
    return Array.from(groups.entries());
  }, [flatItems]);

  const eventTypeOptions = useMemo(() => {
    const availableTypes = new Set(flatItems.map((item) => item.eventType));
    const options: Array<{ value: string; label: string }> = [];
    for (const group of EVENT_GROUPS) {
      if (group.eventTypes.some((value) => availableTypes.has(value))) {
        options.push({ value: group.value, label: group.label });
      }
    }
    for (const item of flatItems) {
      if (!options.some((option) => option.value === item.eventType)) {
        options.push({ value: item.eventType, label: formatEventLabel(item.eventType) });
      }
    }
    return options;
  }, [flatItems]);

  useEffect(() => {
    if (!sentinelRef.current || !feedQuery.hasNextPage || feedQuery.isFetchingNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        feedQuery.fetchNextPage().catch(() => undefined);
      }
    });

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [feedQuery]);

  const setInfiniteData = (
    updater: (data: InfiniteData<FeedResponse> | undefined) => InfiniteData<FeedResponse> | undefined,
  ) => {
    queryClient.setQueryData<InfiniteData<FeedResponse>>(queryKey, updater);
  };

  const createNoteMutation = useMutation({
    mutationFn: async (body: string) =>
      sendJson<NoteResponse>("POST", `/api/crm/${entityType}/${entityId}/notes`, { body }),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<InfiniteData<FeedResponse>>(queryKey);
      const tempId = `temp-${Date.now()}`;
      const optimisticItem: FeedItem = {
        id: tempId,
        itemType: "note",
        entityType,
        entityId,
        occurredAt: new Date().toISOString(),
        actorUserId: currentUserId,
        actorName: "You",
        eventType: "note",
        title: "Note",
        body,
        noteId: tempId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isEditable: true,
      };

      setInfiniteData((existing) => {
        if (!existing) return existing;
        const [firstPage, ...rest] = existing.pages;
        return {
          ...existing,
          pages: [
            { ...firstPage, items: [optimisticItem, ...firstPage.items] },
            ...rest,
          ],
        };
      });

      setNewNote("");
      return { tempId, previous };
    },
    onSuccess: (created, _body, context) => {
      setInfiniteData((existing) => {
        if (!existing) return existing;
        return {
          ...existing,
          pages: existing.pages.map((page) => ({
            ...page,
            items: page.items.map((item) =>
              item.id === context?.tempId
                ? {
                    ...item,
                    id: created.id,
                    noteId: created.id,
                    body: created.body,
                    createdAt: created.createdAt,
                    updatedAt: created.updatedAt,
                  }
                : item,
            ),
          })),
        };
      });
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Note added" });
    },
    onError: (error, _body, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ noteId, body }: { noteId: string; body: string }) =>
      sendJson<NoteResponse>("PATCH", `/api/crm/${entityType}/${entityId}/notes/${noteId}`, { body }),
    onMutate: async ({ noteId, body }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<InfiniteData<FeedResponse>>(queryKey);
      setInfiniteData((existing) => {
        if (!existing) return existing;
        return {
          ...existing,
          pages: existing.pages.map((page) => ({
            ...page,
            items: page.items.map((item) =>
              item.noteId === noteId
                ? {
                    ...item,
                    body,
                    updatedAt: new Date().toISOString(),
                  }
                : item,
            ),
          })),
        };
      });
      return { previous };
    },
    onSuccess: () => {
      setEditingNoteId(null);
      setEditValue("");
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Note updated" });
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) =>
      sendJson<void>("DELETE", `/api/crm/${entityType}/${entityId}/notes/${noteId}`),
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<InfiniteData<FeedResponse>>(queryKey);
      setInfiniteData((existing) => {
        if (!existing) return existing;
        return {
          ...existing,
          pages: existing.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item.noteId !== noteId),
          })),
        };
      });
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Note deleted" });
    },
    onError: (error, _noteId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleApplyFilters = () => {
    setDateRange(draftDateRange);
    setEventType(draftEventType);
    setActor(draftActor);
    setEntityFilter(draftEntityFilter);
  };

  const handleResetFilters = () => {
    setDraftDateRange("all");
    setDraftEventType("all");
    setDraftActor("all");
    setDraftEntityFilter("all");
    setDateRange("all");
    setEventType("all");
    setActor("all");
    setEntityFilter("all");
  };

  const hasActiveFilters =
    search.trim() || eventType !== "all" || actor !== "all" || dateRange !== "all";

  const emptyStateCopy = hasActiveFilters
    ? "No matches for current filters"
    : isEmailLens
      ? "No communications yet"
      : "No activity recorded yet";

  const emptyStateDetail = hasActiveFilters
    ? `Reset or widen the filters to see more ${isEmailLens ? "communications" : "activity"}.`
    : isEmailLens
      ? `Linked emails will appear here for this ${ENTITY_LABELS[entityType].toLowerCase()}.`
      : `Activity will appear here as changes are made to this ${ENTITY_LABELS[entityType].toLowerCase()}.`;

  return (
    <div className="space-y-3">
      <Card data-testid={testId}>
        <CardHeader className="py-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-base font-medium">
              {isEmailLens ? "Communications" : "Activity"}
            </CardTitle>
            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start gap-2" data-testid="button-activity-filters">
                    <Filter className="h-4 w-4" />
                    Filters
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 space-y-3">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Date range</p>
                    <Select value={draftDateRange} onValueChange={(value: DateRange) => setDraftDateRange(value)}>
                      <SelectTrigger data-testid="filter-date-range">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_RANGE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Entity type</p>
                    <Select value={draftEntityFilter} onValueChange={setDraftEntityFilter}>
                      <SelectTrigger data-testid="filter-entity-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All entities</SelectItem>
                        <SelectItem value={entityType}>{ENTITY_LABELS[entityType]}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Event type</p>
                    <Select value={draftEventType} onValueChange={setDraftEventType}>
                      <SelectTrigger data-testid="filter-event-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All events</SelectItem>
                        {eventTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Actor</p>
                    <Select value={draftActor} onValueChange={setDraftActor}>
                      <SelectTrigger data-testid="filter-actor">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All actors</SelectItem>
                        {actorOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={handleResetFilters}>
                      Reset
                    </Button>
                    <Button size="sm" onClick={handleApplyFilters}>
                      Apply
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search activity"
                  className="pl-9 md:w-64"
                  data-testid="input-activity-search"
                />
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {!isEmailLens ? (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <MessageSquare className="h-4 w-4" />
                Add note
              </div>
              <Textarea
                value={newNote}
                onChange={(event) => setNewNote(event.target.value)}
                placeholder="Write a note..."
                className="min-h-[100px]"
                data-testid="activity-feed-note-input"
              />
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  disabled={!newNote.trim() || createNoteMutation.isPending}
                  onClick={() => createNoteMutation.mutate(newNote.trim())}
                  data-testid="button-post-note"
                >
                  {createNoteMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                  Post note
                </Button>
              </div>
            </div>
          ) : null}

          {feedQuery.isLoading ? (
            <div className="space-y-2 py-2">
              <div className="h-16 animate-pulse rounded-md bg-muted" />
              <div className="h-16 animate-pulse rounded-md bg-muted" />
              <div className="h-16 animate-pulse rounded-md bg-muted" />
            </div>
          ) : feedQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">
              <p className="font-medium">Activity feed failed to load</p>
              <p className="mt-1 text-muted-foreground">
                {(feedQuery.error as Error).message}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => feedQuery.refetch()}
              >
                <RefreshCw className="mr-1 h-3 w-3" />
                Retry
              </Button>
            </div>
          ) : flatItems.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center">
              <p className="text-sm font-medium">{emptyStateCopy}</p>
              <p className="mt-1 text-xs text-muted-foreground">{emptyStateDetail}</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="activity-timeline">
              {groupedItems.map(([label, items]) => (
                <div key={label} className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground" data-testid="date-group-header">
                    {label}
                  </p>
                  {items.map((item) =>
                    item.itemType === "email" ? (
                      <div key={item.id} data-testid="activity-entry">
                        <EmailThreadItem
                          testId="communications-email-item"
                          item={{
                            id: item.id,
                            threadId: item.threadId ?? null,
                            occurredAt: item.occurredAt,
                            subject: item.subject ?? item.title,
                            preview: item.preview ?? item.body,
                            direction: item.direction ?? null,
                            messageCount: item.messageCount,
                            hasAttachments: item.hasAttachments,
                            outlookWebLink: item.outlookWebLink,
                            from: item.actorName
                              ? { emailAddress: { name: item.actorName } }
                              : undefined,
                          }}
                        />
                      </div>
                    ) : item.itemType === "note" ? (
                      <div key={item.id} data-testid="activity-entry">
                        <NoteItem
                          item={item}
                          editValue={editValue}
                          isEditing={editingNoteId === item.noteId}
                          isSaving={updateNoteMutation.isPending}
                          onStartEdit={(selected) => {
                            setEditingNoteId(selected.noteId);
                            setEditValue(selected.body ?? "");
                          }}
                          onCancelEdit={() => {
                            setEditingNoteId(null);
                            setEditValue("");
                          }}
                          onEditValueChange={setEditValue}
                          onSaveEdit={() => {
                            if (!item.noteId) return;
                            updateNoteMutation.mutate({ noteId: item.noteId, body: editValue.trim() });
                          }}
                          onDelete={(selected) => {
                            if (!selected.noteId) return;
                            deleteNoteMutation.mutate(selected.noteId);
                          }}
                        />
                      </div>
                    ) : (
                      <div key={item.id} data-testid="activity-entry">
                        <div
                          className="rounded-md border p-3"
                          data-testid={entityType === "account" ? "account-activity-entry" : undefined}
                        >
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" data-testid="event-type-badge">
                              {formatEventLabel(item.eventType)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {item.actorName || item.actorUserId || "System"} · {formatOccurredAt(item.occurredAt)}
                            </span>
                          </div>
                          <p className="mt-2 text-sm">{item.title}</p>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ))}

              <div ref={sentinelRef} className="h-1" />

              {feedQuery.isFetchingNextPage ? (
                <div className="flex items-center justify-center py-2 text-xs text-muted-foreground">
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Loading more
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
